import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCurrency } from '../../utils/format';
import {
  faTimes,
  faBox
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

const InfoRow = ({ label, value, isBig = false }) => {
  const { t } = useTranslation();
  const emDash = t('common.symbols.emDash');
  const displayValue = (value !== null && value !== undefined && value !== '') ? value : emDash;

  if (value === null || value === undefined || value === '') return null;

  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.MUTED }}>
        {label}
      </span>
      <span className={`font-medium mt-0.5 leading-tight ${isBig ? 'text-base' : 'text-[13px]'}`} style={{ color: UI_COLORS.TEXT.PRIMARY }}>
        {displayValue}
      </span>
    </div>
  );
};

const formatDate = (val, emDash) => {
  if (!val) return emDash;
  try {
    return new Date(val).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return emDash;
  }
};

// NOTE: despite the name this is used for freight/subtotal/GST/total as well
// as prices, so it must keep formatCurrency's default ceiling. Only the
// per-line unit price opts out — see the Price cell below.
const formatPrice = (price) => formatCurrency(price);

const InquiryDetails = ({ isOpen, onClose, inquiry, loading = false }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const strings = t('inquiriesModule.inquiries.view', { returnObjects: true });
  const tableCopy = t('inquiriesModule.inquiries.table.copy', { returnObjects: true });
  const emDash = t('common.symbols.emDash');
  // Compact unit labels (kg / m / mts / pcs), keyed by the stored unit value.
  const unitLabels = t('common.unitOptionsShort', { returnObjects: true }) || {};

  // Derive display values: prefer denormalized names if present, otherwise fall back to id placeholders
  const customerName = inquiry?.customer_name || inquiry?.customer?.name || (strings.copy?.noData || emDash);
  const customerCompany = inquiry?.customer_company || inquiry?.customer?.company || (strings.copy?.noData || emDash);
  const deliveryLocation = inquiry?.delivery_location_name || inquiry?.delivery_location?.name || (strings.copy?.noData || emDash);

  const freight = formatPrice(inquiry?.freight ?? null);
  const subtotal = inquiry?.subtotal != null ? formatPrice(inquiry.subtotal) : (strings.copy?.noPrice || t('common.symbols.currency') + '0.00');
  const gstAmount = inquiry?.gst_amount != null ? formatPrice(inquiry.gst_amount) : (strings.copy?.noPrice || t('common.symbols.currency') + '0.00');
  const total = inquiry?.total != null ? formatPrice(inquiry.total) : (strings.copy?.noPrice || t('common.symbols.currency') + '0.00');

  const expectedDelivery = inquiry?.expected_delivery_date || emDash;
  const statusKey = inquiry?.status || '';
  const statusLabel = tableCopy.statuses?.[statusKey] || (statusKey ? statusKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : emDash);

  const isNewCustomerLabel = inquiry?.is_new_customer ? tableCopy.newCustomerLabel : tableCopy.existingCustomerLabel;
  const includeGstLabel = inquiry?.include_gst ? 'Yes' : 'No';

  // Created by: prefer the BE-provided label ("Customer" for portal, else the
  // sales exec's name) so we never special-case a null created_by.
  const createdByName = inquiry?.created_by_label || inquiry?.created_by_name || inquiry?.created_by_user?.name || inquiry?.created_by || emDash;
  const isPortalSource = (inquiry?.source || '').toLowerCase() === 'portal';

  // Get items array
  const items = Array.isArray(inquiry?.items) ? inquiry.items : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.inquiry-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .inquiry-view-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div
        className="inquiry-view-modal w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {strings.header.sectionTitle}
            </p>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {inquiry?.id ? `Inquiry #${inquiry.id}` : strings.header.fallbackTitle}
              </h2>
              {inquiry && (
                <span
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                  style={{
                    backgroundColor: colorHelpers.withOpacity(isPortalSource ? UI_COLORS.STATUS.WARNING : UI_COLORS.TEXT.SECONDARY, 0.12),
                    color: isPortalSource ? UI_COLORS.STATUS.WARNING : UI_COLORS.TEXT.SECONDARY,
                  }}
                >
                  {isPortalSource
                    ? `${strings.copy?.customer || 'Customer'}${inquiry?.customer_company ? ` · ${inquiry.customer_company}` : ''}`
                    : `${strings.fields?.createdBy || 'By'} ${createdByName}`}
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{customerName}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
              ></div>
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.loading}
              </p>
            </div>
          ) : !inquiry ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon
                icon={faBox}
                className="text-4xl opacity-60"
                style={{ color: UI_COLORS.TEXT.MUTED }}
              />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {strings.empty.title}
              </h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.empty.message}
              </p>
            </div>
          ) : (
            <>
              {/* Main Info Grid */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-3 md:grid-cols-4 p-3 md:p-4 rounded-xl border bg-gray-50/30 mb-6" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <InfoRow label={strings.fields.customerName} value={customerName} />
                <InfoRow label={strings.fields.customerCompany} value={customerCompany} />
                <InfoRow label={strings.fields.deliveryLocation} value={deliveryLocation} />
                <InfoRow label={strings.fields.createdBy} value={createdByName} />
                
                <InfoRow label={strings.fields.expectedDeliveryDate} value={formatDate(expectedDelivery, emDash)} />
                <InfoRow label={strings.fields.status} value={statusLabel} />
                <InfoRow label={strings.fields.isNewCustomer} value={isNewCustomerLabel} />
                <InfoRow label="Include GST" value={includeGstLabel} />
              </div>

              {/* Items Table */}
              <div className="mt-8">
                <h3 className="text-sm font-semibold uppercase tracking-wide mb-4 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                  <FontAwesomeIcon icon={faBox} className="text-xs" />
                  {strings.fields.items || 'Items'} ({items.length})
                </h3>

                <div className="rounded-xl border overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                      <thead>
                        <tr className="border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.1) }}>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">#</th>
                          <th className="px-4 py-4 text-xs font-bold uppercase tracking-wider text-gray-500">Product / Size</th>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Pcs</th>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Qty</th>
                          <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-500">Price</th>
                          <th className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Unit</th>
                          {/* "Amount", not "Total": this column is the ex-GST
                              line value (price x quantity), so the lines sum to
                              the Subtotal below rather than to the grand Total.
                              Matches the quotation PDF's Amount column. */}
                          <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-500">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y" style={{ divideColor: UI_COLORS.BORDERS.LIGHT }}>
                        {items.length > 0 ? (
                          items.map((it, idx) => (
                            <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                              <td className="px-4 py-4 text-center text-xs font-medium text-gray-400 align-top">
                                {idx + 1}
                              </td>
                              <td className="px-4 py-4 align-top space-y-1">
                                <p className="text-sm font-semibold text-wrap max-w-xs" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                  {it.product_alias || it.product_name || `Item ${idx + 1}`}
                                </p>
                                <div className="flex flex-wrap gap-2 items-center text-wrap max-w-xs">
                                  {it.category_name && (
                                    <span className="text-[10px] text-gray-400">
                                      {it.category_name}
                                    </span>
                                  )}
                                  {it.hsn_code && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-gray-600 border border-gray-100">
                                      HSN: {it.hsn_code}
                                    </span>
                                  )}
                                </div>
                                  {it.remark && (
                                    <div className="mt-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-800 border border-yellow-200 uppercase tracking-tight">
                                      {it.remark}
                                    </div>
                                  )}
                              </td>
                              <td className="px-4 py-4 text-center align-top font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {(() => {
                                  const pcs = it.quantity_in_pieces ?? it.pieces ?? it.quantity_per_pieces;
                                  return (pcs !== undefined && pcs !== null && pcs !== '' && Number(pcs) !== 0) ? pcs : emDash;
                                })()}
                              </td>
                              <td className="px-4 py-4 text-center align-top font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {(it.quantity !== undefined && it.quantity !== null && it.quantity !== '') ? it.quantity : emDash}
                              </td>
                              <td className="px-4 py-4 text-right align-top font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {it.price ? formatCurrency(it.price, { round: false }) : emDash}
                              </td>
                              <td className="px-4 py-4 text-center align-top text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {it.units ? (unitLabels[it.units] || it.units) : emDash}
                              </td>
                              <td className="px-4 py-4 text-right align-top font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {(() => {
                                  // Total excluding GST. Prefer the API's subtotal
                                  // (price x quantity); fall back to computing it.
                                  const net = it.subtotal
                                    ?? ((it.price && it.quantity) ? Number(it.price) * Number(it.quantity) : null);
                                  return (net !== null && net !== undefined && !Number.isNaN(Number(net)))
                                    ? formatCurrency(net)
                                    : emDash;
                                })()}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="px-4 py-12 text-center text-gray-400">
                              No items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Summary and Totals */}
              {/* Summary and Totals Component */}
              <div className="mt-8 border-t pt-8" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                  {/* Left: Remarks */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                      Remarks & Notes
                    </h4>
                    <p className="text-xs italic leading-relaxed" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                      {inquiry?.remark || 'No additional remarks.'}
                    </p>
                  </div>
                  
                  {/* Right: Compact Totals */}
                  <div className="w-full md:w-80 space-y-2 p-4 rounded-xl" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.05) }}>
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-[12px]">
                        <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.fields.subtotal}</span>
                        <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{subtotal}</span>
                      </div>
                      <div className="flex justify-between items-center text-[12px]">
                        <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.fields.gstAmount}</span>
                        <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{gstAmount}</span>
                      </div>
                      <div className="flex justify-between items-center text-[12px] pb-1.5 border-b mb-1" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.fields.freight}</span>
                        <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{freight}</span>
                      </div>
                      <div className="flex justify-between items-center pt-1 mt-1">
                        <span className="text-sm font-bold uppercase tracking-tight" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.fields.total}</span>
                        <span className="text-lg font-black text-blue-600">
                          {total}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <button
            onClick={onClose}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105 outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              backgroundColor: 'transparent',
              color: UI_COLORS.TEXT.MUTED,
              border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
            }}
          >
            {t('common.buttons.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InquiryDetails;
